/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import * as _ from 'lodash';
import { styled as materialStyled } from '@mui/material/styles';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Pagination,
  PaginationItem,
  Stack,
  Typography,
} from '@mui/material';
import GridTile, { GridTileLoading } from './GridTile';
import { theme } from '../../../theme/theme';
import ExpandMoreSvg from '../../../assets/img/arrow-down.svg';
import { IGem } from '../../../redux/gem/gemSlice';
import { Field } from '../../../pages/MyGemVault/MyGemVault';

const CustomPagination = materialStyled(Pagination)(() => ({
  fontFamily: 'Inter',
  fontSize: '15px',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: 'normal',
  '& .MuiPaginationItem-root': {
    borderRadius: '0px',
    border: `1px solid ${theme.palette.shades.grayBorder}`,
    margin: '0',
  },
  '& .Mui-selected': {
    backgroundColor: `${theme.palette.shades.lightBlueColor} !important`,
    fontWeight: 700,
    borderRadius: '0px',
    border: `1px solid ${theme.palette.shades.lightBlueColor}`,
    color: 'white',
    '&:hover': { '&.Mui-selected': { backgroundColor: theme.palette.secondary.light } },
  },
  '& .MuiPaginationItem-root:not(.Mui-selected)': {
    border: '1px solid rgba(0, 0, 0, 0.2)',
    color: theme.palette.text.secondary,
  },
  '& .MuiPaginationItem-previousNext': {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontWeight: 600,
    color: theme.palette.primary.main,
    padding: '6px 12px',
  },
  '& .MuiPaginationItem-ellipsis': {
    display: 'flex',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    color: theme.palette.primary.main,
  },
}));

export interface GemGridViewProps {
  isLoading: boolean;
  gems: any[];
  totalData: number;
  enablePagination: boolean;
  selectedPage: number;
  enablePagePerTile: boolean;
  tilesPerList: number[];
  selectedTilesPerPage: number;
  expandedColumns: string[];
  expandedColumnValues: string[];
  handleChangePage: (_page: number) => void;
  handleChangeTilesPerPage: (_rowsPerPage: number) => void;
  handleClickFullDetailButton: (_gemId: string) => void;
  bottomResultsCount?: number;
  handleFavourite: (_gemId: string, _favId: string) => void;
  handleTogglePublic: (_gemId: string) => void;
  handleEditGem: (_gem: IGem) => void;
  topRighField?: Field;
  isGemBank?: boolean;
  showPublicIcon?: boolean;
}
const GemGridView = ({
  isLoading,
  gems,
  totalData,
  enablePagination,
  selectedPage,
  enablePagePerTile,
  tilesPerList,
  selectedTilesPerPage,
  expandedColumns,
  expandedColumnValues,
  handleChangePage,
  handleChangeTilesPerPage,
  handleClickFullDetailButton,
  handleFavourite,
  handleTogglePublic,
  bottomResultsCount,
  handleEditGem,
  topRighField,
  isGemBank,
}: GemGridViewProps) => {
  const [anchorElRowsPerPage, setAnchorElRowsPerPage] = React.useState<null | HTMLElement>(null);

  const getDisplayedRange = () => {
    const start = selectedPage === 1 ? 1 : (selectedPage - 1) * selectedTilesPerPage + 1;
    const end = Math.min(totalData, selectedPage * selectedTilesPerPage);
    return `${start} - ${end} of ${totalData} Gems`;
  };

  return (
    <Grid container sx={{ paddingTop: '10px' }}>
      <Grid container spacing={2} justifyContent="flex-start">
        {gems.map((gem) =>
          isLoading ? (
            <GridTileLoading key={gem.gemId} />
          ) : (
            <Grid item xs={12} md={4} sm={6} key={gem.gemId} data-testid="grid-tile">
              <GridTile
                gem={gem}
                handleClickFullDetailButton={() => handleClickFullDetailButton(gem.gemId)}
                columnValues={expandedColumnValues}
                columns={expandedColumns}
                handleFavourite={() => handleFavourite(gem.gemId, gem.favouriteId)}
                handleEditGem={() => handleEditGem(gem)}
                handleTogglePublic={() => handleTogglePublic(gem.gemId)}
                topRighField={topRighField}
                isGemBank={isGemBank}
                showPublicIcon={!!gem.organizationId && gem.is_scanned}
              />
            </Grid>
          ),
        )}
      </Grid>
      {!isLoading && (
        <Grid container spacing={1} pt={2.5}>
          <Grid item xs={_.isUndefined(bottomResultsCount) ? 6 : 4} display="flex" justifyContent="flex-start">
            <Box>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                padding={1}
                gap={2}
                border={`1px solid ${theme.palette.shades.grayBorder}`}
                borderRadius="8px"
              >
                {enablePagePerTile && (
                  <>
                    <Typography variant="tableRowRegular" color="shades.common.black">
                      Show {selectedTilesPerPage}
                    </Typography>
                    <IconButton
                      aria-label="row-per-page-expand-btn"
                      onClick={(event) => setAnchorElRowsPerPage(event.currentTarget)}
                      size="small"
                      sx={{ p: 0 }}
                      role="button"
                    >
                      <Box component="img" src={ExpandMoreSvg} />
                    </IconButton>
                    <Menu
                      aria-label="menu-appbar"
                      anchorEl={anchorElRowsPerPage}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      keepMounted={false}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      open={Boolean(anchorElRowsPerPage)}
                      onClose={() => setAnchorElRowsPerPage(null)}
                      role="menu"
                    >
                      {tilesPerList.map((e) => (
                        <MenuItem
                          id={`record-per-row-${e}`}
                          selected={e === selectedTilesPerPage}
                          onClick={() => {
                            handleChangeTilesPerPage(e);
                            setAnchorElRowsPerPage(null);
                          }}
                          key={e}
                          role="menuitem"
                        >
                          <Typography variant="meta" color="info" textAlign="center">
                            {e}
                          </Typography>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
                <Divider orientation="vertical" flexItem sx={{ marginY: -1 }} />
                <Typography variant="tableRowRegular" color="shades.common.black">
                  {getDisplayedRange()}
                </Typography>
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={_.isUndefined(bottomResultsCount) ? 6 : 4} display="flex" justifyContent="flex-end">
            {enablePagination && (
              <CustomPagination
                data-testid="pagination"
                size="medium"
                page={selectedPage}
                count={totalData && selectedTilesPerPage ? Math.ceil(totalData / selectedTilesPerPage) : 1}
                siblingCount={0}
                boundaryCount={1}
                onChange={(event, page) => handleChangePage(page)}
                renderItem={(item) => (
                  <PaginationItem
                    data-testid="pagination_btn"
                    {...item}
                    components={{
                      previous: () => <span>&larr; Prev</span>,
                      next: () => <span>Next &rarr;</span>,
                    }}
                  />
                )}
              />
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

export default GemGridView;
